// ============================================================
// PlatformTenantIntegrationsPage.js
// Route /platform/tenant-integrations — Platform Management, internal
// A2Z IT operational tooling. Global view across ALL clients' M365
// tenant integrations (Broad + Selected).
//
// Mirrors PlatformAnalyticsPage.js: per-section {data, loading, error},
// parallel fetch. Two sources: listTenantIntegrations (the rows) +
// getTenantIntegrationsMetrics (search activity over a window, merged
// into rows by tenantIntegrationId). Read-only. No drill-through to
// TenantSearchLog detail (the per-client Tenant Search tab owns that).
// ============================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Badge from '../components/Badge';
import { listTenantIntegrations, getTenantIntegrationsMetrics } from '../api/adminTenantIntegrations';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';
import StatusMessage from '../components/StatusMessage';
import './PlatformPages.css';
import './PlatformTenantIntegrationsPage.css';

const STATUS_OPTIONS = ['active', 'pending', 'revoked', 'expired'];
const WINDOW_OPTIONS = [7, 30, 90];
const STATUS_VARIANT = { active: 'success', pending: 'warning', revoked: 'neutral', expired: 'danger' };

function pct(rate) {
  return typeof rate === 'number' ? `${Math.round(rate * 100)}%` : '—';
}

function MetricTile({ label, value }) {
  return (
    <div className="ti-tile">
      <div className="ti-tile__value">{value}</div>
      <div className="ti-tile__label">{label}</div>
    </div>
  );
}

// Derived "Search" cell per the 2026-06-12 selected-scope realignment:
// Selected never lives-searches — it is served by Phase 4 indexing, which
// SHIPPED 2026-08-09. The cell now reports the real indexing state rather
// than a standing "pending" that has stopped being true.
function searchState(it) {
  if (it.scopeModel === 'selected') {
    return it.isIndexingActive
      ? { text: 'Indexed · active', muted: false }
      : { text: 'Indexed · off', muted: true };
  }
  if (it.status === 'active' && it.isSearchActive) return { text: 'Live · active', muted: false };
  if (it.status === 'active' && !it.isSearchActive) return { text: 'Live · paused', muted: false };
  return { text: '—', muted: true };
}

function scopedSitesLabel(it) {
  const n = Array.isArray(it.scopedSites) ? it.scopedSites.length : 0;
  if (it.scopeModel === 'broad' && n === 0) return 'all sites';
  if (n > 0) return `${n} site${n === 1 ? '' : 's'}`;
  return '—';
}

function failureClass(n) {
  if (n >= 5) return 'ti-fail ti-fail--red';
  if (n >= 3) return 'ti-fail ti-fail--amber';
  return 'ti-fail';
}

export default function PlatformTenantIntegrationsPage() {
  const navigate = useNavigate();
  const [list, setList] = useState({ data: null, loading: true, error: null });
  const [metrics, setMetrics] = useState({ data: null, loading: true, error: null });

  // Filters
  const [windowDays, setWindowDays] = useState(7);
  const [selectedStatuses, setSelectedStatuses] = useState(() => new Set(STATUS_OPTIONS));
  const [scopeFilter, setScopeFilter] = useState('both');
  const [showDemo, setShowDemo] = useState(true); // operational default ON (inverse of analytics IsDemo=0)

  const fetchList = useCallback(async () => {
    setList({ data: null, loading: true, error: null });
    try { setList({ data: await listTenantIntegrations(), loading: false, error: null }); }
    catch (err) { setList({ data: null, loading: false, error: translateError(err) }); }
  }, []);

  const fetchMetrics = useCallback(async (days) => {
    setMetrics({ data: null, loading: true, error: null });
    try { setMetrics({ data: await getTenantIntegrationsMetrics({ days }), loading: false, error: null }); }
    catch (err) { setMetrics({ data: null, loading: false, error: translateError(err) }); }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchMetrics(windowDays); }, [fetchMetrics, windowDays]);

  const items = list.data ? list.data.items : [];

  // tenantIntegrationId -> per-integration metric entry (merged into rows).
  const metricsMap = useMemo(() => {
    const m = new Map();
    (metrics.data ? metrics.data.perIntegration : []).forEach((e) => m.set(e.tenantIntegrationId, e));
    return m;
  }, [metrics.data]);

  // Tiles reflect the GLOBAL state (full unfiltered list + platform totals);
  // the filters below affect the table only.
  const totalIntegrations = items.length;
  const activeBroad = items.filter((it) => it.status === 'active' && it.scopeModel === 'broad').length;
  const activeSelected = items.filter((it) => it.status === 'active' && it.scopeModel === 'selected').length;
  const totals = metrics.data ? metrics.data.platformTotals : null;

  const filtered = items.filter((it) =>
    selectedStatuses.has(it.status)
    && (scopeFilter === 'both' || it.scopeModel === scopeFilter)
    && (showDemo || !it.isDemo)
  );
  const anyFilterApplied = selectedStatuses.size < STATUS_OPTIONS.length || scopeFilter !== 'both' || !showDemo;

  function toggleStatus(s) {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  }

  return (
    <>
      <PageHeader
        title="Tenant Integrations"
        subtitle="Global operational view of all client M365 tenant integrations."
      />

      {/* (1) Metrics block — 6 tiles */}
      <div className="ti-tiles">
        <MetricTile label="Total integrations" value={list.loading ? '…' : totalIntegrations} />
        <MetricTile label="Active Broad" value={list.loading ? '…' : activeBroad} />
        <MetricTile label="Active Selected" value={list.loading ? '…' : activeSelected} />
        <MetricTile label={`Search volume — last ${windowDays}d`} value={metrics.loading ? '…' : (totals ? totals.totalSearches : '—')} />
        <MetricTile label={`Cache hit rate — last ${windowDays}d`} value={metrics.loading ? '…' : pct(totals && totals.cacheHitRate)} />
        <MetricTile label={`Error rate — last ${windowDays}d`} value={metrics.loading ? '…' : pct(totals && totals.errorRate)} />
      </div>
      {metrics.error && <StatusMessage as="p" variant="error" className="ti-state ti-state--error">{metrics.error}</StatusMessage>}

      {/* (2) Controls */}
      <div className="ti-controls">
        <div className="ti-control">
          <span className="ti-control__label">Window</span>
          {WINDOW_OPTIONS.map((d) => (
            <button key={d} type="button" className={`ti-chip ${windowDays === d ? 'ti-chip--on' : ''}`} onClick={() => setWindowDays(d)}>{d}d</button>
          ))}
        </div>
        <div className="ti-control">
          <span className="ti-control__label">Status</span>
          {STATUS_OPTIONS.map((s) => (
            <label key={s} className="ti-check">
              <input type="checkbox" checked={selectedStatuses.has(s)} onChange={() => toggleStatus(s)} /> {s}
            </label>
          ))}
        </div>
        <div className="ti-control">
          <span className="ti-control__label">Scope</span>
          {['both', 'broad', 'selected'].map((sc) => (
            <button key={sc} type="button" className={`ti-chip ${scopeFilter === sc ? 'ti-chip--on' : ''}`} onClick={() => setScopeFilter(sc)}>
              {sc === 'both' ? 'Both' : sc === 'broad' ? 'Broad' : 'Selected'}
            </button>
          ))}
        </div>
        <label className="ti-check ti-control__demo">
          <input type="checkbox" checked={showDemo} onChange={(e) => setShowDemo(e.target.checked)} /> Show demo clients
        </label>
      </div>

      {/* (3) Table */}
      <section className="ti-table-wrap">
        {list.loading ? (
          <StatusMessage as="p" variant="loading" className="ti-state">Loading…</StatusMessage>
        ) : list.error ? (
          <StatusMessage as="p" variant="error" className="ti-state ti-state--error">{list.error}</StatusMessage>
        ) : filtered.length === 0 ? (
          <StatusMessage as="p" variant="empty" className="ti-state">No tenant integrations match the current filters.{anyFilterApplied ? ' Try clearing a filter.' : ''}</StatusMessage>
        ) : (
          <table className="ti-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Scope</th>
                <th>Status</th>
                <th>Search</th>
                <th>Scoped sites</th>
                <th>Failures</th>
                <th>Last search</th>
                <th>Onboarded</th>
                {showDemo && <th>Demo</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => {
                const ss = searchState(it);
                const metric = metricsMap.get(it.tenantIntegrationId);
                const lastSearch = metric && metric.lastSearchAt ? formatDate(metric.lastSearchAt) : '—';
                return (
                  <tr
                    key={it.tenantIntegrationId}
                    className="ti-row"
                    onClick={() => navigate(`/clients/${it.clientId}?tab=tenant-search`)}
                    title="Open this client's Tenant Search tab"
                  >
                    <td><span className="ti-client__name">{it.clientName}</span> <span className="ti-client__id">#{it.clientId}</span></td>
                    <td><Badge variant={it.scopeModel === 'broad' ? 'blue' : 'purple'}>{it.scopeModel === 'broad' ? 'Broad' : 'Selected'}</Badge></td>
                    <td><Badge variant={STATUS_VARIANT[it.status] || 'neutral'}>{it.status}</Badge></td>
                    <td className={ss.muted ? 'ti-muted' : ''}>{ss.text}</td>
                    <td>{scopedSitesLabel(it)}</td>
                    <td><span className={failureClass(it.consecutiveFailureCount || 0)}>{it.consecutiveFailureCount || 0}</span></td>
                    <td>{lastSearch}</td>
                    <td>{it.consentGrantedAt ? formatDate(it.consentGrantedAt) : '—'}</td>
                    {showDemo && <td>{it.isDemo ? <span className="ti-demo-pill">DEMO</span> : ''}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
